/**
 * Phosphor persistence as a two-target feedback loop.
 *
 * Runs at buffer resolution with nearest filtering so a trail stays pixel
 * aligned with the sprite that made it. The HUD is a DOM layer above the
 * canvas and never enters this buffer, so nothing here can smear the UI.
 *
 * The decay itself is in `shaders/persistence.ts`, written in seconds. This
 * class owns the two targets, the swap, and the seconds it hands the shader.
 */

import * as THREE from 'three';
import { CRT } from '@/core/config';
import { PERSISTENCE_FRAG } from '../shaders/persistence';
import { FullscreenPass, clearTarget, createTarget } from './FullscreenPass';

export class Persistence {
  private targets: [THREE.WebGLRenderTarget, THREE.WebGLRenderTarget];
  private index = 0;
  private readonly pass: FullscreenPass;
  private readonly type: THREE.TextureDataType;
  private primed = false;
  /** Per-channel monomolecular tau in seconds; folded into uMonoDecay per frame. */
  private readonly tau: [number, number, number] = [...CRT.phosphorTau];
  /** True while the accumulators are freed because the governor holds the pass off. */
  private released = false;
  /** Extent to recreate at, kept current by resize() even while released. */
  private width: number;
  private height: number;

  constructor(width: number, height: number, type: THREE.TextureDataType) {
    this.type = type;
    this.width = width;
    this.height = height;
    this.targets = [
      createTarget(width, height, { type }),
      createTarget(width, height, { type }),
    ];

    this.pass = new FullscreenPass(PERSISTENCE_FRAG, {
      tCurrent: { value: null },
      tPrev: { value: null },
      uDt: { value: 1 / 60 },
      uMonoDecay: { value: new THREE.Vector3(0, 0, 0) },
      uRate: { value: new THREE.Vector3(...CRT.phosphorRate) },
      uTrailSaturation: { value: CRT.trailSaturation },
      uFloor: { value: CRT.persistenceFloor },
    });
  }

  /** The accumulated afterglow. The composite maxes this against the live frame. */
  get texture(): THREE.Texture {
    return this.targets[this.index].texture;
  }

  /** Monomolecular time constants, per channel, in seconds. */
  setTau(r: number, g: number, b: number): void {
    this.tau[0] = Math.max(1e-3, r);
    this.tau[1] = Math.max(1e-3, g);
    this.tau[2] = Math.max(1e-3, b);
  }

  /** Bimolecular decay rates, per channel, in inverse seconds per root-intensity. */
  setRate(r: number, g: number, b: number): void {
    (this.pass.uniforms.uRate.value as THREE.Vector3).set(
      Math.max(0, r),
      Math.max(0, g),
      Math.max(0, b),
    );
  }

  set trailSaturation(value: number) {
    this.pass.uniforms.uTrailSaturation.value = value;
  }

  set floor(value: number) {
    this.pass.uniforms.uFloor.value = Math.max(0, value);
  }

  resize(width: number, height: number): void {
    // Released: remember the extent and stop. The governor switched this pass
    // off; reallocating two half-float accumulators for it on every region
    // change would be paying rent on an empty room.
    this.width = width;
    this.height = height;
    if (this.released) return;
    if (width === this.targets[0].width && height === this.targets[0].height) return;
    this.targets[0].dispose();
    this.targets[1].dispose();
    this.targets = [
      createTarget(width, height, { type: this.type }),
      createTarget(width, height, { type: this.type }),
    ];
    this.primed = false;
  }

  /**
   * Frees both accumulators while the governor holds the pass off.
   *
   * At a 2x region that is ~7.4MB of half-float sitting resident for a pass
   * that renders nothing. `render` recreates them at the remembered extent, so
   * switching the rung back on costs one allocation and an invalidate — which
   * the caller's `persistedLast` latch forces anyway.
   */
  release(): void {
    if (this.released) return;
    this.released = true;
    this.targets[0].dispose();
    this.targets[1].dispose();
    this.primed = false;
  }

  /** Wipes both buffers. Call on resize, on scene changes, and before a capture. */
  reset(renderer: THREE.WebGLRenderer): void {
    clearTarget(renderer, this.targets[0]);
    clearTarget(renderer, this.targets[1]);
    this.primed = true;
  }

  /**
   * Books a wipe for the next `render`, rather than doing one now.
   *
   * The distinction is not fussiness. `reset` issues two clears against the
   * current GL state, and the callers that want to invalidate the trail — a
   * resize observer, a DPR change — run *outside* any frame, where those clears
   * do not survive to the next draw. The symptom is the whole tube going black:
   * the feedback loop reads whatever was in the reallocated half-float target,
   * `CrtComposite` takes `max(current, trail)` of it, and the frame never
   * recovers because the poison is now the trail. Deferring puts the wipe inside
   * `render`, in the same GL state the loop itself runs in, which is where it
   * has always worked.
   */
  invalidate(): void {
    this.primed = false;
  }

  /** `dt` is the seconds of decay to apply, not a frame count. */
  render(renderer: THREE.WebGLRenderer, current: THREE.Texture, dt: number): void {
    if (this.released) {
      this.released = false;
      this.targets = [
        createTarget(this.width, this.height, { type: this.type }),
        createTarget(this.width, this.height, { type: this.type }),
      ];
      this.primed = false;
    }
    if (!this.primed) this.reset(renderer);

    const prev = this.targets[this.index];
    const next = this.targets[1 - this.index];

    this.pass.uniforms.uDt.value = dt;
    // exp(-dt/tau) is a frame constant; three per-channel exponentials here
    // instead of three per pixel at region resolution.
    (this.pass.uniforms.uMonoDecay.value as THREE.Vector3).set(
      Math.exp(-dt / this.tau[0]),
      Math.exp(-dt / this.tau[1]),
      Math.exp(-dt / this.tau[2]),
    );
    this.pass.uniforms.tCurrent.value = current;
    this.pass.uniforms.tPrev.value = prev.texture;
    this.pass.render(renderer, next);

    this.index = 1 - this.index;
  }

  dispose(): void {
    this.targets[0].dispose();
    this.targets[1].dispose();
    this.pass.dispose();
  }
}
